import type { Rate, RateCategory, RateSchedule } from "../types";

/** A schedule's category, treating a missing value as the persisted default. */
export const rateScheduleCategory = (schedule: RateSchedule): RateCategory =>
  (schedule.rateCategory ?? "current") as RateCategory;

/** The current-category schedules — the ones the grid displays and edits inline. */
export const currentSchedules = (
  schedules: RateSchedule[] | undefined,
): RateSchedule[] =>
  (schedules ?? []).filter(
    (schedule) => rateScheduleCategory(schedule) === "current",
  );

/**
 * Schedules for a plan's primary (non-AD&D) section. AD&D lives in its own schedule
 * (rateCoverageIndicator === "adnd") and is rendered separately, but an AD&D rate can share
 * a slot key with a primary rate — e.g. a life "Rate per $1,000" and an AD&D "Rate per
 * $1,000" both have no tier and the same unit. A primary section must derive its renewal
 * lookup from these schedules only, or the AD&D rate collides onto the primary slot.
 */
export const primarySectionSchedules = (
  schedules: RateSchedule[] | undefined,
): RateSchedule[] =>
  (schedules ?? []).filter(
    (schedule) => schedule.rateCoverageIndicator !== "adnd",
  );

/**
 * The renewal-family schedule shown in the grid for a current plan. Revised
 * renewal takes precedence over renewal (AC: only revised displays when both
 * exist); returns undefined when neither is present.
 */
export const displayedRenewalSchedule = (
  schedules: RateSchedule[] | undefined,
):
  | {
      category: Extract<RateCategory, "renewal" | "revisedRenewal">;
      schedule: RateSchedule;
    }
  | undefined => {
  const list = schedules ?? [];
  const revised = list.find(
    (schedule) => rateScheduleCategory(schedule) === "revisedRenewal",
  );
  if (revised) return { category: "revisedRenewal", schedule: revised };

  const renewal = list.find(
    (schedule) => rateScheduleCategory(schedule) === "renewal",
  );
  if (renewal) return { category: "renewal", schedule: renewal };

  return undefined;
};

/**
 * Maps each coverage-tier code to its rate value within a schedule. Rates without a
 * coverage-tier code are skipped so they can't collapse into a single shared key.
 */
export const ratesByTier = (
  schedule: RateSchedule | undefined,
): Record<string, number | undefined> =>
  Object.fromEntries(
    (schedule?.rates ?? []).flatMap((rate) =>
      rate.coverageTierCode != null
        ? [[rate.coverageTierCode, rate.rate] as const]
        : [],
    ),
  );

/**
 * Stable structural identity for a rate "slot" — coverage tier + age band + gender +
 * tobacco status. Uniquely locates a rate within a plan so a renewal rate lines up with
 * the current-plan slot it inherits from. Collapses to the tier code for a plain tiered
 * rate, so tier-keyed callers can migrate to it with no change.
 */
export const slotKeyOf = (rate: Rate): string =>
  [
    rate.coverageTierCode,
    rate.ageBandLower != null ? `l${rate.ageBandLower}` : null,
    rate.ageBandUpper != null ? `u${rate.ageBandUpper}` : null,
    rate.gender,
    rate.isTobaccoRated != null
      ? rate.isTobaccoRated
        ? "tob"
        : "nontob"
      : null,
  ]
    .filter(Boolean)
    .join("-");

/**
 * Slot identity that also folds in `unit`. FMLA PEPM stacks two rates (fmlaPepm, adaPepm)
 * in the same tier/gender slot that differ only by unit; without unit they collapse to one
 * key. The grid uses this for those designs; the drawer stays on the tier-only `slotKeyOf`.
 */
export const slotKeyWithUnit = (rate: Rate): string =>
  [slotKeyOf(rate), rate.unit].filter(Boolean).join("-");

/** Maps each slot key to its rate value across all schedules in the given category. */
const ratesBySlotForCategory = (
  schedules: RateSchedule[] | undefined,
  category: RateCategory,
  keyOf: (rate: Rate) => string,
): Record<string, number | undefined> =>
  Object.fromEntries(
    (schedules ?? [])
      .filter((schedule) => rateScheduleCategory(schedule) === category)
      .flatMap((schedule) =>
        (schedule.rates ?? []).map((rate) => [keyOf(rate), rate.rate] as const),
      ),
  );

/** Per-slot renewal-display lookups for a plan's full (all-category) schedule list. */
export interface RenewalColumns {
  /**
   * False when the plan has no renewal/revised schedule — the renewal maps
   * (columnBySlot/renewalBySlot/revisedBySlot) are then empty, though currentBySlot may
   * still be populated. Renderers gate the whole renewal column on this.
   */
  showRenewal: boolean;
  /** "Rev. Ren." when revised renewal is present, else "Renewal". */
  columnLabel: string;
  /** The value shown in the renewal column (revised takes precedence over renewal). */
  columnBySlot: Record<string, number | undefined>;
  /** Every populated set, for the per-slot tooltip. */
  currentBySlot: Record<string, number | undefined>;
  renewalBySlot: Record<string, number | undefined>;
  revisedBySlot: Record<string, number | undefined>;
  /** Each slot's rate unit, so renewal values format at the same precision as the current
   * cells (formatRateValue picks 2- vs 3-decimal precision from the unit and the plan's
   * coverage type). */
  unitBySlot: Record<string, string | undefined>;
}

/**
 * Renewal-display lookups for a plan's full (all-category) schedule list. Renderers use
 * these to show a read-only renewal column and per-slot tooltip beside the current rate.
 * `showRenewal` is false for every carrier column and any incumbent with none entered,
 * so those render exactly as before.
 */
export const getRenewalColumns = (
  rateSchedules: RateSchedule[] | undefined,
  keyOf: (rate: Rate) => string = slotKeyOf,
): RenewalColumns => {
  const info = displayedRenewalSchedule(rateSchedules);
  const renewalBySlot = ratesBySlotForCategory(rateSchedules, "renewal", keyOf);
  const revisedBySlot = ratesBySlotForCategory(
    rateSchedules,
    "revisedRenewal",
    keyOf,
  );

  // A slot's unit is stable across categories, so any schedule's rate resolves it; used to
  // format renewal values at the same precision as the current cells.
  const unitBySlot: Record<string, string | undefined> = Object.fromEntries(
    (rateSchedules ?? []).flatMap((schedule) =>
      (schedule.rates ?? []).map((rate) => [keyOf(rate), rate.unit] as const),
    ),
  );

  return {
    showRenewal: info !== undefined,
    columnLabel: info?.category === "revisedRenewal" ? "Rev. Ren." : "Renewal",
    columnBySlot:
      info?.category === "revisedRenewal" ? revisedBySlot : renewalBySlot,
    currentBySlot: ratesBySlotForCategory(rateSchedules, "current", keyOf),
    renewalBySlot,
    revisedBySlot,
    unitBySlot,
  };
};
